/**
 * @file MeetingFeedbackPrompt.tsx
 * @brief Post-meeting rating — mobile FeedbackSheet parity (web).
 *
 * @details
 * Two-step FormSheet matching mobile (29-07-2026):
 *  1) Main — mood slider + Submit; optional “Add details”
 *  2) Details — comment + meetingintel issue chips when rating ≤ 3
 * Auto-prompts once when the meeting is ready and this user has no row yet;
 * “Edit your feedback” re-opens with pre-fill (GET own session feedback only).
 *
 * @dependencies
 * - FormSheet, EmotionRatingSlider, feedbackEndpoints
 *
 * @references
 * - FRONTEND/src/shared/components/FeedbackSheet.tsx
 * - docs/product/meeting-intelligence.md (Post-session feedback)
 * - BACKEND/src/features/feedback/routes.js
 *
 * @author DES
 * @date 28-07-2026
 * @updated 30-07-2026 — mood slider + details step + edit pre-fill
 */

import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { ApiClientError, apiGet, apiPost } from "@/shared/api/client";
import { feedbackEndpoints } from "@/shared/api/endpoints";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { EmotionRatingSlider } from "./EmotionRatingSlider";

type ExistingFeedback = {
  overall_rating?: number;
  tags?: string[] | null;
  comment?: string | null;
};

type TagOption = { key: string; label: string };

/** Same keys/labels as mobile FeedbackSheet meetingintel tags. */
const MEETING_TAGS: TagOption[] = [
  { key: "missing_transcript", label: "Missing transcript" },
  { key: "wrong_speaker", label: "Wrong speaker" },
  { key: "wrong_translation", label: "Wrong translation" },
  { key: "summary_quality", label: "Summary quality" },
  { key: "audio_quality", label: "Audio quality" },
];

type Step = "main" | "details" | "done";

type Props = {
  meetingId: string;
  meetingReady: boolean;
};

export function MeetingFeedbackPrompt({ meetingId, meetingReady }: Props) {
  const [open, setOpen] = useState(false);
  const [hasFeedback, setHasFeedback] = useState(false);
  const [rating, setRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [step, setStep] = useState<Step>("main");
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [checked, setChecked] = useState(false);
  /** Snapshot for thanks copy — set before flipping hasFeedback on submit. */
  const [doneAsUpdate, setDoneAsUpdate] = useState(false);

  const isLow = rating > 0 && rating <= 3;
  const hasComment = comment.trim().length > 0;
  const detailsHintParts: string[] = [];
  if (isLow && selectedTags.length > 0) {
    detailsHintParts.push(
      selectedTags.length === 1
        ? "1 issue"
        : `${selectedTags.length} issues`,
    );
  }
  if (hasComment) detailsHintParts.push("note added");
  const detailsHint =
    detailsHintParts.length > 0 ? detailsHintParts.join(" · ") : undefined;

  const applyExisting = (existing: ExistingFeedback) => {
    const r = existing.overall_rating;
    setRating(typeof r === "number" && r >= 1 && r <= 5 ? r : 0);
    setSelectedTags(
      Array.isArray(existing.tags)
        ? existing.tags.filter((t): t is string => typeof t === "string")
        : [],
    );
    setComment(
      typeof existing.comment === "string" ? existing.comment : "",
    );
  };

  useEffect(() => {
    if (!isLow) setSelectedTags([]);
  }, [isLow]);

  useEffect(() => {
    if (!meetingReady || !meetingId) return;
    let cancelled = false;
    void (async () => {
      try {
        const raw = await apiGet<{ feedback?: ExistingFeedback | null }>(
          feedbackEndpoints.sessionById(meetingId),
        );
        if (cancelled) return;
        if (raw?.feedback) {
          setHasFeedback(true);
          applyExisting(raw.feedback);
          setChecked(true);
          return;
        }
        setChecked(true);
        setOpen(true);
      } catch {
        if (!cancelled) {
          setChecked(true);
          setOpen(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingId, meetingReady]);

  const resetTransient = () => {
    setStep("main");
    setSaving(false);
    setLoadingExisting(false);
    setDoneAsUpdate(false);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      resetTransient();
      if (!hasFeedback) {
        setRating(0);
        setSelectedTags([]);
        setComment("");
      }
    }
  };

  const openForEdit = async () => {
    setStep("main");
    setOpen(true);
    setLoadingExisting(true);
    try {
      const raw = await apiGet<{ feedback?: ExistingFeedback | null }>(
        feedbackEndpoints.sessionById(meetingId),
      );
      if (raw?.feedback) {
        setHasFeedback(true);
        applyExisting(raw.feedback);
      }
    } catch {
      // Keep whatever we already have from the initial check.
    } finally {
      setLoadingExisting(false);
    }
  };

  const toggleTag = (key: string) => {
    setSelectedTags((prev) =>
      prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key],
    );
  };

  const submit = async () => {
    if (rating < 1) {
      toast({
        variant: "destructive",
        title: "Pick a rating",
        description: "Slide to choose a mood.",
      });
      return;
    }
    setSaving(true);
    try {
      await apiPost(feedbackEndpoints.session, {
        sessionId: meetingId,
        sessionType: "meetingintel",
        overallRating: rating,
        tags: isLow ? selectedTags : [],
        comment: comment.trim() || null,
        platform: "web",
      });
      setDoneAsUpdate(hasFeedback);
      setHasFeedback(true);
      setStep("done");
      window.setTimeout(() => {
        handleOpenChange(false);
      }, 900);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Couldn’t save feedback",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
      setSaving(false);
    }
  };

  if (!meetingReady || !checked) return null;

  return (
    <>
      {hasFeedback && !open ? (
        <button
          type="button"
          className="text-xs font-medium text-muted-foreground underline-offset-2 hover:underline"
          onClick={() => void openForEdit()}
        >
          Edit your feedback
        </button>
      ) : null}

      <FormSheet open={open} onOpenChange={handleOpenChange}>
        {/* Up from the bottom, centred, as tall as it needs (DES 30-09-2026:
            "open from the bottom like a mobile action sheet, but ziyada
            lambi na ho, also corner main bhi na ho"). */}
        <FormSheetContent side="bottom">
          <FormSheetFrame>
            {step === "done" ? (
              <>
                <FormSheetHeader>
                  <FormSheetTitle>
                    {doneAsUpdate
                      ? "Feedback updated!"
                      : "Thanks for your feedback!"}
                  </FormSheetTitle>
                  <FormSheetDescription>
                    Your input helps us improve.
                  </FormSheetDescription>
                </FormSheetHeader>
                <FormSheetBody>
                  <div className="flex flex-col items-center justify-center gap-3 px-6 py-16">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/12 text-emerald-600 dark:text-emerald-400">
                      <Check className="h-8 w-8" strokeWidth={2.5} />
                    </div>
                  </div>
                </FormSheetBody>
              </>
            ) : step === "details" ? (
              <>
                <FormSheetHeader>
                  <FormSheetTitle>A bit more detail</FormSheetTitle>
                  <FormSheetDescription>
                    Optional. Helps us improve Verblizr faster.
                  </FormSheetDescription>
                </FormSheetHeader>

                <FormSheetBody>
                  <FormSheetFields className="grid-cols-1">
                    <FormSheetField span={2}>
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Tell us more
                      </p>
                      <textarea
                        value={comment}
                        onChange={(e) =>
                          setComment(e.target.value.slice(0, 2000))
                        }
                        placeholder={
                          isLow
                            ? "What could we do better?"
                            : "What worked well for you?"
                        }
                        rows={6}
                        maxLength={2000}
                        className="w-full resize-none bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                      />
                      <p
                        className={cn(
                          "mt-2 text-right text-[11px] font-medium text-muted-foreground opacity-70",
                          comment.length >= 1800 && "text-primary opacity-100",
                        )}
                      >
                        {comment.length} / 2000
                      </p>
                    </FormSheetField>

                    {isLow ? (
                      <FormSheetField span={2}>
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          What went wrong?
                        </p>
                        <p className="mb-3 text-xs text-muted-foreground">
                          Optional. Pick any that apply.
                        </p>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                          {MEETING_TAGS.map((t) => {
                            const active = selectedTags.includes(t.key);
                            return (
                              <button
                                key={t.key}
                                type="button"
                                aria-pressed={active}
                                onClick={() => toggleTag(t.key)}
                                className={cn(
                                  "inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium transition-colors",
                                  active
                                    ? "bg-primary/10 text-primary"
                                    : "bg-muted text-muted-foreground hover:bg-muted/80",
                                )}
                              >
                                {active ? (
                                  <Check className="h-3.5 w-3.5 shrink-0" />
                                ) : null}
                                <span className="truncate">{t.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </FormSheetField>
                    ) : null}
                  </FormSheetFields>
                </FormSheetBody>

                <FormSheetFooter className="grid-cols-1">
                  <FormSheetFooterCell>
                    <Button
                      type="button"
                      onClick={() => setStep("main")}
                      disabled={saving}
                      className={cn(
                        formSheetActionClass,
                        "bg-primary text-primary-foreground hover:bg-primary/90",
                      )}
                    >
                      Done
                    </Button>
                  </FormSheetFooterCell>
                </FormSheetFooter>
              </>
            ) : (
              <>
                <FormSheetHeader>
                  <FormSheetTitle>How was this meeting?</FormSheetTitle>
                  <FormSheetDescription>
                    {hasFeedback
                      ? "Update your rating below"
                      : "Slide to rate. Details are optional."}
                  </FormSheetDescription>
                </FormSheetHeader>

                <FormSheetBody>
                  {loadingExisting ? (
                    <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                      Loading…
                    </div>
                  ) : (
                    <FormSheetSection title="Rating">
                      <FormSheetFields className="grid-cols-1">
                        <FormSheetField span={2}>
                          <EmotionRatingSlider
                            rating={rating}
                            onChange={setRating}
                          />
                          {rating >= 1 ? (
                            <div className="mt-1 flex flex-col items-center gap-0.5 pb-2">
                              <button
                                type="button"
                                className="text-sm font-semibold text-primary"
                                onClick={() => setStep("details")}
                              >
                                {detailsHint ? "Edit details" : "Add details"}
                              </button>
                              {detailsHint ? (
                                <span className="text-[11px] text-muted-foreground">
                                  {detailsHint}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                        </FormSheetField>
                      </FormSheetFields>
                    </FormSheetSection>
                  )}
                </FormSheetBody>

                {!loadingExisting ? (
                  <FormSheetFooter>
                    <FormSheetFooterCell>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => handleOpenChange(false)}
                        disabled={saving}
                        className={formSheetActionClass}
                      >
                        Later
                      </Button>
                    </FormSheetFooterCell>
                    <FormSheetFooterCell>
                      <Button
                        type="button"
                        onClick={() => void submit()}
                        disabled={saving || rating < 1}
                        className={cn(
                          formSheetActionClass,
                          "bg-primary text-primary-foreground hover:bg-primary/90",
                        )}
                      >
                        {saving
                          ? "Saving…"
                          : hasFeedback
                            ? "Update feedback"
                            : "Submit feedback"}
                      </Button>
                    </FormSheetFooterCell>
                  </FormSheetFooter>
                ) : null}
              </>
            )}
          </FormSheetFrame>
        </FormSheetContent>
      </FormSheet>
    </>
  );
}
